import { ManagedSurveyAssetType } from "../constants/managedSurveyAssetTypes";
import {
  CablePDFilterName,
  InsulationType,
  MeasurementAccessory,
  MeasurementComponent,
  MeasurementType,
  PhaseReferenceType,
  RAGStatus,
  ReportStatuses,
  SubLocation,
  SwitchPosition,
  TEVInterpretation,
  UltraSonicClassification,
} from "../enums/Result";
import { CableNaming, SubstationType } from "../enums/UTP2";
import { Data } from "./Data";

/**
 * Represents the result contained within a 360 data object, as returned by the results API.
 * */
export interface Result extends Data<ResultValue> {
  switchGearName?: string;
  status?: ReportStatuses;
}

export interface ReportStatus {
  captureId: number;
  status: ReportStatuses;
  firstViewedUserId: string;
  created: Date;
  updated: Date;
}

export interface ReportStatusUpdate {
  captureId: number;
  status: ReportStatuses;
  customerId?: number;
  hvSubstationId?: number;
  hvSubstationName?: string;
}

export interface ResultWithReportInclusionStatus extends Result {
  report: boolean;
}

export interface ResultAssetAndCaptureIds {
  assetId: number;
  captureId: number;
}

export type Measurement =
  | CablePDMeasurement
  | TEVMeasurement
  | UHFMeasurement
  | UltraSonicMeasurement
  | VPISMeasurement;

export interface ResultResubmissionPanel {
  panelNo: string;
  panel360AssetId: number;
  panelName?: string;
  isExcluded: boolean;
}

export interface ResultResubmissionSwitchgear {
  switchGearId: number;
  assetType: string;
  panels: ResultResubmissionPanel[];
}

export interface ResultResubmission {
  hvSubstationId: number;
  switchGearId: number;
  originalFileName?: string;
  savedFileName?: string;
  captureId: number;
  panelNos: string[];
  excludedPanelNos: string[];
  panelAssetIds?: number[];
  excludedPanelAssetIds?: number[];
  workOrderId?: number;
  switchGears?: ResultResubmissionSwitchgear[];
}

export interface ResultFileUpload {
  ResultFile: File;
  Notes?: string;
  CustomerId?: number;
  HVSubstationId?: number;
  SwitchGearId?: number;
}

export interface WorkOrderResultUpload {
  workOrderId: number;
  customerId: number;
  resultFile: File;
  notes?: string;
  startDate: Date;
  completionDate: Date;
}

export const MaximumFileSizeBytes = 524288000;

export interface MeasurementDetail<T> {
  measurement: T;
  measurementType: MeasurementType;
  panelName: string;
  panelNo: string;
  panel360AssetId: number;
  time?: string;
  captureId?: number;
  instrument?: Instrument;
}

/**
 * Represents the complete 360 result once all files for this measurement from the device have been processed.
 * */
export interface ResultValue {
  OriginalFileName: string;
  SavedFileName: string;
  MetaData: MetaData;
  Assets: ResultSurveyableAsset[];
  PanelMeasurements: PanelMeasurement[];
  MediaMetaData: MediaMetaData[];
  OverallRAGStatus: RAGStatus;
  SubstationSummary?: SubstationSummary;
}

export interface MetaData {
  JobGroup: JobGroup;
  SubstationGroup: SubstationGroup;
  SwitchGearGroup: SwitchGearGroup;
  HvSurveyableAssetGroups: HvSurveyableAssetGroup[];
  SubstationConditionsGroup: SubstationConditionsGroup;
  SurveyName: string;
  CustomGroups: SurveyFields[];
  Instrument: Instrument;
}

export interface Instrument {
  $CAL_DUE: string;
  $DNA: string;
  $FIRMWARE_VERSION: string;
  $HARDWARE_VERSION: number;
  $SELF_CHECK: string;
  $SERIAL: string;
  "Cable PD PILC thresholds": RAGThresholds;
  "Cable PD XLPE thresholds": RAGThresholds;
  "TEV Thresholds": RAGThresholds;
  "Ultrasonic thresholds": RAGThresholds;
}

export interface RAGThresholds {
  Red: number;
  Amber: number;
  Green: number;
}

export interface SurveyFields {
  fields: Field[];
  group: string;
}

export interface BaseGroup {
  CustomQuestions: Field[];
}

export interface Field {
  data: string;
  fieldname: string;
}

export interface JobGroup extends BaseGroup {
  JobNo: string;
  EngineerName: string;
}

export interface SubstationGroup extends BaseGroup {
  SubstationName: string;
  SubstationType: SubstationType;
  CableNamingScheme: CableNaming;
}
export interface SubstationSummary extends BaseGroup {
  SubstationId: number;
  SubstationName: string;
}

export interface SwitchGearGroup extends BaseGroup {
  Manufacturer: string;
  OperatingVoltage: string;
  Insulation: string;
  InstallDate: string;
  RatedVoltage: string;
  Type: string;
}

export interface HvSurveyableAssetGroup extends SwitchGearGroup {
  AssetName: string;
  AssetType: string;
}

export interface SubstationConditionsGroup extends BaseGroup {
  BackMetalDbReading: string;
  BackMetalPPCReading: string;
  BackAirDbReading: string;
  BackAirPPCReading: string;
  TemperatureCelsius: string;
  RelativeHumidity: string;
}

/**
 * Represents metadata for supporting media files captured during the survey
 */
export interface MediaMetaData {
  SurveyAssetName: string;
  AssetId: number;
  Comment: string;
  DataPointId: number;
  MediaFilePath: string;
  OriginalFilePath: string;
  Time: Date;
  url: string;
  mimeType: string | undefined;
  loading: boolean;
  error: Response | undefined;
}

/**
 * Contains all the panels for a surveyable asset
 */
export interface ResultSurveyableAsset {
  AssetId: number;
  AssetName: string;
  AssetType: ManagedSurveyAssetType;
  PanelMeasurements: PanelMeasurement[];
}

/**
 * Contains all the measurements for a panel
 */
export interface PanelMeasurement {
  Panel360AssetId: number;
  Panel360AssetName: string;
  PanelNo: string;
  PanelName: string;
  TEVMeasurements: TEVMeasurement[];
  UHFMeasurements: UHFMeasurement[];
  CablePDMeasurements: CablePDMeasurement[];
  UltraSonicMeasurements: UltraSonicMeasurement[];
  VPISMeasurements: VPISMeasurement[];
}

/**
 * Measurement data that is recorded on all measurement types.
 */
export interface BaseMeasurement {
  ComponentAssetId: number;
  ComponentType: MeasurementComponent;
  Insulation: InsulationType;
  MeasurementType: MeasurementType;
  SwitchPosition: SwitchPosition;
  SubLocation: SubLocation;
  Comments: string;
  PhaseReferenceLock: boolean;
  PhaseReferenceType: PhaseReferenceType;
  Wav8kDataPoint: number;
  Wav8kFilePath: string;
  MeasurementFiles: string[];
  RAGStatus: RAGStatus;
  WaveFormScalingFactors?: WaveformScalingFactors;
}

export interface WaveformScalingFactors {
  Interval: number;
  Offset: number;
  Scale: number;
  XStart: number;
}

/**
 * Represents a TEV measurement.
 */

export interface TEVMeasurement extends BaseMeasurement {
  MeasureDB: number;
  MeasurePPC: number;
  NoiseLevel: number;
  TEVInterpretation: TEVInterpretation;
  EventsDataPoint: number;
  WaveformsDataPoint: number;
}

/**
 * Represents a UHF measurement.
 */

export interface UHFMeasurement extends BaseMeasurement {
  MeasureDBM: number;
  Gain: number;
  Frequency: number;
  Baseline: number;
  EventsDataPoint: number;
  WaveformsDataPoint: number;
  WidebandMode: boolean;
}

/**
 * Represents a Cable PD measurement.
 */
export interface CablePDMeasurement extends BaseMeasurement {
  MeasurePC: number;
  MeasurePPC: number;
  FilterName: CablePDFilterName;
  CTModel: string;
  EventsDataPoint: number;
  WaveformsDataPoint: number;
}

/**
 * Represents an UltraSonic measurement.
 */

export interface UltraSonicMeasurement extends BaseMeasurement {
  MeasureDBUV: number;
  UltraSonicClassification: UltraSonicClassification;
  UltraSonicAccessory: MeasurementAccessory;
  UltraSonicClassificationCertainty: number;
  PhasePlotDataPoint: number;
  Wav10kDataPoint: number;
  Wav10kFilePath: string;
  Wav192kDataPoint: number;
  Wav192kFilePath: string;
}

/**
 * Represents a VPIS measurement.
 */
export interface VPISMeasurement extends BaseMeasurement {
  MeasureMV: number;
  MeasurePPC: number;
  EventsDataPoint: number;
  WaveformsDataPoint: number;
}
